import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { homeService } from "../services";
import defaultImage from "../assets/images/cuisines/default.jpeg";

const SearchResult = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("keyword") || searchParams.get("q") || "";

  const [loading, setLoading] = useState(false);
  const [restaurants, setRestaurants] = useState<any[]>([]);
  const [dishes, setDishes] = useState<any[]>([]);

  useEffect(() => {
    if (!keyword.trim()) {
      setRestaurants([]);
      setDishes([]);
      return;
    }

    const fetchResults = async () => {
      setLoading(true);
      try {
        const res: any = await homeService.search(keyword.trim(), 20, 20);
        const data = res?.data || res;
        setRestaurants(data?.restaurants || data?.restaurant || []);
        setDishes(data?.dishes || data?.dish || []);
      } catch (error) {
        console.error("Search failed:", error);
        setRestaurants([]);
        setDishes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [keyword]);

  return (
    <div className="bg-white dark:bg-gray-900 min-h-screen transition-colors duration-300 mt-[7.5rem] md:mt-[72px]">
      <div className="max-w-[1400px] mx-auto p-4 md:p-6">
        <h1 className="text-[#050505] dark:text-white font-urbanist text-[24px] font-semibold mb-2">
          Search results{keyword ? ` for “${keyword}”` : ""}
        </h1>
        <p className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-sm mb-6">
          {loading
            ? "Searching..."
            : `${restaurants.length} restaurants · ${dishes.length} dishes`}
        </p>

        {loading ? (
          <div className="py-20 text-center text-gray-500 dark:text-gray-400">
            Loading...
          </div>
        ) : !keyword.trim() ? (
          <div className="py-20 text-center text-gray-500 dark:text-gray-400">
            Enter a search term to find restaurants and dishes.
          </div>
        ) : restaurants.length === 0 && dishes.length === 0 ? (
          <div className="py-20 text-center text-gray-500 dark:text-gray-400">
            No results found.
          </div>
        ) : (
          <div className="space-y-10">
            {restaurants.length > 0 && (
              <section>
                <h2 className="text-[#050505] dark:text-white font-urbanist text-[20px] font-semibold mb-4">
                  Restaurants
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {restaurants.map((r: any) => (
                    <div
                      key={r._id || r.restaurantId}
                      onClick={() =>
                        navigate(`/restaurants/${r._id || r.restaurantId}`)
                      }
                      className="border dark:border-gray-700 rounded-[16px] overflow-hidden cursor-pointer hover:shadow-md transition-shadow bg-white dark:bg-gray-800"
                    >
                      <img
                        src={
                          r.kitchenPhotos?.[0] ||
                          r.image ||
                          r.coverImage ||
                          defaultImage
                        }
                        alt={r.businessName || r.name}
                        className="w-full h-40 object-cover"
                      />
                      <div className="p-4">
                        <h3 className="text-[#050505] dark:text-white font-urbanist text-[17px] font-semibold">
                          {r.businessName || r.name}
                        </h3>
                        <p className="text-[#6A6A6A] dark:text-gray-400 text-sm mt-1">
                          {r.location?.city ||
                            r.location?.address ||
                            r.cuisine ||
                            ""}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {dishes.length > 0 && (
              <section>
                <h2 className="text-[#050505] dark:text-white font-urbanist text-[20px] font-semibold mb-4">
                  Dishes
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {dishes.map((d: any) => (
                    <div
                      key={d._id}
                      onClick={() => {
                        const rid =
                          d.restaurantId ||
                          d.restaurant?._id ||
                          d.restaurant?.restaurantId;
                        if (rid) navigate(`/restaurants/${rid}`);
                      }}
                      className="border dark:border-gray-700 rounded-[16px] overflow-hidden cursor-pointer hover:shadow-md transition-shadow bg-white dark:bg-gray-800 flex gap-3 p-3"
                    >
                      <img
                        src={d.images?.[0]?.url || d.image || defaultImage}
                        alt={d.dishName || d.name}
                        className="w-20 h-20 rounded-[12px] object-cover"
                      />
                      <div>
                        <h3 className="text-[#050505] dark:text-white font-urbanist text-[16px] font-semibold">
                          {d.dishName || d.name}
                        </h3>
                        <p className="text-[#7402A7] dark:text-purple-400 font-medium mt-1">
                          ₹{d.basePrice ?? d.price ?? 0}
                        </p>
                        {d.restaurant?.businessName && (
                          <p className="text-[#6A6A6A] dark:text-gray-400 text-xs mt-1">
                            {d.restaurant.businessName}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResult;
